"use client";

import React from "react";
import { Typography, Container, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { useLocale, useTranslations } from "next-intl";
import FAQData from "../json/FAQ.json";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

interface FAQItem {
  id: number;
  question_en: string;
  answer_en: string;
  question_ch: string;
  answer_ch: string;
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `0px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
  padding: theme.spacing(1, 3),
  marginBottom: theme.spacing(2),
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(({ theme }) => ({
  flexDirection: "row-reverse",
  padding: theme.spacing(2, 0),
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    color: theme.palette.$TCPG400,
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(2),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 0, 2, 0),
}));

export default function FAQ() {
  const t = useTranslations("Menu");
  const locale = useLocale();
  const isEnglish = locale === "en";
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Box id="faq" sx={{ pb: {xs: 5, md: 10}, pt: {xs: 5, md: 10, lg: 20} }}>
      <Container maxWidth="lg" data-aos="fade-up">
        <Typography
          variant="h1"
          component="h1"
          sx={{
            textAlign: "center",
            mb: {xs:3, md: 5}
          }}>
          {t("FAQ")}
        </Typography>
        {FAQData.map((item: FAQItem, index: number) => (
          <Accordion
            className="accordion-item"
            key={item.id}
            expanded={expanded === `panel${index}`}
            onChange={handleChange(`panel${index}`)}
          >
            <AccordionSummary
              expandIcon={
                expanded === `panel${index}` ? (
                  <RemoveIcon fontSize="large" />
                ) : (
                  <AddIcon fontSize="large" />
                )
              }
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Typography variant="h6" data-heading-tag="H6">
                {isEnglish ? item.question_en : item.question_ch}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                className="answer-box"
                dangerouslySetInnerHTML={{
                  __html: isEnglish ? item.answer_en : item.answer_ch,
                }}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>
    </Box>
  );
}
